import Cross from "@icons/cross.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import invariant from "tiny-invariant";

import {
  canSignCheckFn,
  useGuard,
} from "@/common/acl/action-guard/action-guard";
import { Button } from "@/common/components/button/button";
import { Dialog, useDialogProvider } from "@/common/components/dialog/dialog";
import { InputField } from "@/common/components/form/input-field/input-field";
import { Failed } from "@/common/components/info/info";
import { Fields, OrderForm } from "@/common/components/order-form/order-form";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { Spinner } from "@/common/components/spinner/spinner";
import { useLanguage } from "@/common/hooks/use-language";
import { TradeType } from "@/generated/digitalnisklady.cz/graphql";
import { getUnit } from "@/routes/shared/utils/crop-unit";
import { futureQuartersQuery } from "@/routes/trades/trades.api";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { handleFormError } from "../shared/api/error-handler";
import { useRemoveOrderMutation } from "../shared/api/remove-order";
import { path as failedPath } from "../shared/routes/failed/failed";
import { path as successPath } from "../shared/routes/success/success";
import type { OrderDetail } from "./orders.api";
import { orderDetailQuery, useEditOrderMutation } from "./orders.api";

const path = "offers/:offerId/orders/:orderId";

type MarketplaceFields = {
  quarter: string;
  pricePerUnit: number;
  trader: string;
  amount: number;
  note: string;
};

const MarketplaceOrderForm = ({
  order,
  offerId,
  orderId,
}: {
  order: OrderDetail;
  offerId: string;
  orderId: string;
}) => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const editOrder = useEditOrderMutation();
  const navigate = useNavigate();
  const unit = getUnit(order.crop.unit);
  const quarters = useSuspenseQuery(futureQuartersQuery());

  const form = useForm<MarketplaceFields>({
    defaultValues: {
      amount: order.order.amount,
      note: order.order.note ?? "",
      pricePerUnit: order.order.unitPrice,
      quarter: order.quarter.id,
      trader: order.order.trader?.id ?? "ALL",
    },
  });

  const handleSubmit = (data: MarketplaceFields) => {
    editOrder.mutate(
      {
        id: orderId,
        order: {
          active: true,
          amount: data.amount,
          currency: order.order.currency,
          customTransport: false,
          offer: offerId,
          price: data.pricePerUnit,
          quarter: data.quarter,
          ...(data.trader === "ALL" ? {} : { onlyTrader: data.trader }),
        },
      },
      {
        onError: async (e) => {
          const errors = handleFormError(e);
          await navigate(failedPath + "?" + searchParams.toString(), {
            state: { errors },
          });
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          await navigate(successPath);
        },
      },
    );
  };

  return (
    <>
      <FormProvider {...form}>
        <form
          className="flex flex-col gap-y-6"
          id="marketplace-order-form"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <label className="group" htmlFor="quarter">
            <Trans>Kvartál</Trans>
            <select
              className="group-has-[[role=alert]]:border-can-russet mt-2 h-[calc(--spacing(14)+2px)] w-full rounded-lg p-4 font-bold"
              id="quarter"
              {...form.register("quarter", {
                required: t`Vyberte prosím kvartál`,
              })}
            >
              {quarters.data.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name}
                </option>
              ))}
            </select>
          </label>
          <InputField<MarketplaceFields>
            label={t`Cena za ${unit}`}
            name="pricePerUnit"
            options={{
              min: {
                message: t`Cena musí být kladná`,
                value: 0.01,
              },
              required: t`Vyplňte prosím cenu`,
              valueAsNumber: true,
            }}
            step="0.01"
            type="number"
          />
          <label htmlFor="trader">
            <Trans>Obchodník</Trans>
            <select
              className="mt-2 w-full rounded-lg p-4"
              id="trader"
              {...form.register("trader")}
            >
              <option value="ALL">
                <Trans>Všichni obchodníci</Trans>
              </option>
              {order.order.trader?.id ? (
                <option
                  key={order.order.trader.id}
                  value={order.order.trader.id}
                >
                  {order.order.trader.name}
                </option>
              ) : null}
            </select>
          </label>
          <InputField<MarketplaceFields>
            label={t`Množství` + " (" + unit + ")"}
            name="amount"
            options={{
              min: {
                message: t`Množství musí být kladné`,
                value: 0.01,
              },
              required: t`Vyplňte prosím množství`,
              valueAsNumber: true,
            }}
            type="number"
          />
          <InputField<MarketplaceFields> label={t`Poznámka`} name="note" />
        </form>
      </FormProvider>
      <Button.Container>
        <OrderDeleteButton orderId={orderId} />
        <Button
          disabled={editOrder.status === "pending"}
          form="marketplace-order-form"
          type="submit"
          variant="primary"
        >
          <Trans>Uložit</Trans>
        </Button>
      </Button.Container>
    </>
  );
};

const CommodityOrderForm = ({
  order,
  offerId,
  orderId,
}: {
  order: OrderDetail;
  offerId: string;
  orderId: string;
}) => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const editOrder = useEditOrderMutation();
  const navigate = useNavigate();

  const handleSubmit = (data: Fields) => {
    editOrder.mutate(
      {
        id: orderId,
        order: {
          active: true,
          amount: data.tons,
          currency: order.order.currency,
          customTransport: data.customTransport,
          lowPrice: data.lowPricePerTon,
          offer: offerId,
          price: data.pricePerTon,
          quarter: data.quarter,
          transportNote: data.customTransportNote,
        },
      },
      {
        onError: async (e) => {
          const errors = handleFormError(e);
          await navigate(failedPath + "?" + searchParams.toString(), {
            state: { errors },
          });
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          await navigate(successPath);
        },
      },
    );
  };
  const guardedSubmit = useGuard({
    action: handleSubmit,
    checkFn: canSignCheckFn,
  });

  return (
    <>
      <OrderForm
        initialValues={{
          customTransport: order.order.customTransport,
          customTransportNote: order.order.customTransportNote,
          hasLowPrice: !!order.order.unitLowPrice,
          lowPricePerTon: order.order.unitLowPrice,
          note: order.order.note,
          pricePerTon: order.order.unitPrice,
          quarter: order.quarter.id,
          tons: order.order.amount,
          trader: order.order.trader?.id,
        }}
        key={orderId}
        onSubmit={guardedSubmit}
      >
        <OrderForm.QuarterField />
        <OrderForm.LowPriceField currency={order.order.currency} />
        <OrderForm.PriceField currency={order.order.currency} />
        <OrderForm.TraderField>
          {/* TODO : add all traders */}
          <option value="ALL">
            <Trans>Všichni obchodníci</Trans>
          </option>
          {order.order.trader?.id ? (
            <option key={order.order.trader.id} value={order.order.trader.id}>
              {order.order.trader.name}
            </option>
          ) : null}
        </OrderForm.TraderField>
        <OrderForm.AmountField />
        <OrderForm.CustomTransportField />
        <OrderForm.NoteField />
      </OrderForm>
      <Button.Container>
        <OrderDeleteButton orderId={orderId} />
        <Button
          disabled={editOrder.status === "pending"}
          form={OrderForm.formId}
          type="submit"
          variant="primary"
        >
          <Trans>Uložit</Trans>
        </Button>
      </Button.Container>
    </>
  );
};

const OrderDeleteButton = ({ orderId }: { orderId: string }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const removeOrder = useRemoveOrderMutation();
  const {
    closeDialog: closeConfirmationDialog,
    openDialog: openConfirmationDialog,
  } = useDialogProvider();

  const handleRemoveOrder = () => {
    closeConfirmationDialog();
    removeOrder.mutate(
      { orderId },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          await navigate("..");
        },
      },
    );
  };

  const ConfirmationDialog = (
    <Dialog>
      <Dialog.Title>
        <Trans>Chcete příkaz opravdu smazat?</Trans>
      </Dialog.Title>
      <Dialog.Actions>
        <Dialog.Confirm onClick={handleRemoveOrder}>
          <Trans>Ano</Trans>
        </Dialog.Confirm>
        <Dialog.Cancel onClick={closeConfirmationDialog}>
          <Trans>Ne</Trans>
        </Dialog.Cancel>
      </Dialog.Actions>
    </Dialog>
  );

  return (
    <Button.Delete
      onClick={() => openConfirmationDialog(ConfirmationDialog)}
      type="button"
    >
      <Trans>Smazat příkaz</Trans>
    </Button.Delete>
  );
};

const Order = () => {
  const { offerId, orderId } = useParams();
  invariant(offerId);
  invariant(orderId);
  const locale = useLanguage();
  const order = useQuery(orderDetailQuery({ locale, offerId, orderId }));

  const isMarketplace = order.data?.crop.tradeType === TradeType.ToStorage;

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink to="..">
            <Cross />
          </SearchParamsLink>
        }
      >
        <FullscreenModalLayout.Title>
          <Trans>Detail příkazu</Trans>
        </FullscreenModalLayout.Title>
        {order.status === "pending" ? <Spinner className="m-auto" /> : null}
        {order.status === "error" ? <Failed error={order.error} /> : null}
        {order.status === "success" ? (
          <>
            <PropertiesList
              properties={[
                {
                  label: isMarketplace ? (
                    <Trans>Možný nákup celkem za</Trans>
                  ) : (
                    <Trans>Možný prodej celkem za</Trans>
                  ),
                  value: order.data.order.totalPrice,
                },
                {
                  label: <Trans>Komodita</Trans>,
                  value: order.data.crop.name,
                },
                {
                  label: <Trans>Farma</Trans>,
                  value: order.data.company.name,
                },
                {
                  label: <Trans>Sklad</Trans>,
                  value: order.data.storage.name,
                },
              ]}
            />
            <Spacer className="h-6" />
            {isMarketplace ? (
              <MarketplaceOrderForm
                offerId={offerId}
                order={order.data}
                orderId={orderId}
              />
            ) : (
              <CommodityOrderForm
                offerId={offerId}
                order={order.data}
                orderId={orderId}
              />
            )}
          </>
        ) : null}
      </FullscreenModalLayout>
      <Outlet />
    </>
  );
};

export { Order, path };
